import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const ColorConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // RGB to other formats
    if (fromUnit === 'RGB' && toUnit === 'HEX') {
      return {
        formula: 'HEX = #RRGGBB (where RR, GG, BB are two-digit hexadecimal values)',
        conversionSteps: [
          `Take the RGB values: ${inputValue}`,
          `Convert each component to hexadecimal`,
          `Combine with # prefix: ${result}`
        ],
        realWorldExample: "Pure red in RGB (255, 0, 0) becomes #FF0000 in HEX.",
        funFacts: [
          "HEX colors are widely used in web development and CSS.",
          "Each pair of HEX digits represents 256 possible values (00-FF).",
          "The # symbol in HEX colors is called an octothorpe or hash.",
        ]
      };
    }

    if (fromUnit === 'RGB' && toUnit === 'HSL') {
      return {
        formula: 'Complex conversion involving color wheel mathematics',
        conversionSteps: [
          `Take the RGB values: ${inputValue}`,
          `Calculate Hue (color angle on the color wheel)`,
          `Calculate Saturation (color intensity)`,
          `Calculate Lightness (brightness)`,
          `Result: ${result}`
        ],
        realWorldExample: "RGB(255, 0, 0) converts to HSL(0, 100%, 50%) for pure red.",
        funFacts: [
          "HSL is considered more intuitive for human color perception.",
          "Hue is measured in degrees (0-360) around the color wheel.",
          "Saturation and Lightness are expressed as percentages.",
        ]
      };
    }

    // HEX to other formats
    if (fromUnit === 'HEX' && toUnit === 'RGB') {
      return {
        formula: 'Split HEX into pairs and convert each to decimal (0-255)',
        conversionSteps: [
          `Take the HEX color: ${inputValue}`,
          `Split into R, G, B components`,
          `Convert each from base-16 to decimal`,
          `Result: ${result}`
        ],
        realWorldExample: "#FF0000 converts to RGB(255, 0, 0) for pure red.",
        funFacts: [
          "HEX colors can also include alpha transparency (#RRGGBBAA).",
          "Shorthand HEX notation (#RGB) expands to #RRGGBB.",
          "Web colors often use predefined names like 'red' instead of HEX codes.",
        ]
      };
    }

    // HSL to other formats
    if (fromUnit === 'HSL' && toUnit === 'RGB') {
      return {
        formula: 'Complex conversion involving trigonometric calculations',
        conversionSteps: [
          `Take the HSL values: ${inputValue}`,
          `Convert Hue to RGB color space`,
          `Apply Saturation and Lightness adjustments`,
          `Result: ${result}`
        ],
        realWorldExample: "HSL(0, 100%, 50%) converts to RGB(255, 0, 0) for pure red.",
        funFacts: [
          "HSL makes it easy to create color schemes and variations.",
          "Adjusting Lightness alone creates natural shades and tints.",
          "HSL is particularly useful for color animations.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} in ${fromUnit} format`,
        `Apply color space conversion`,
        `Result: ${result}`
      ],
      realWorldExample: "Color conversions are essential in digital design and web development.",
      funFacts: [
        "The human eye can distinguish about 10 million different colors.",
        "RGB is based on how digital screens create colors.",
        "Different color spaces serve different purposes in digital imaging.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="color"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default ColorConversionExplanation; 